import type { CSSProperties, ReactNode } from 'react'
import { useId, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ITooltip } from 'react-tooltip'
import { Tooltip as ReactTooltip } from 'react-tooltip'

import { cn } from '@/lib/misc/Utils'

type TooltipPositionStrategy = 'absolute' | 'fixed'

export const TOOLTIP_OPEN_HOVER_FOCUS: NonNullable<ITooltip['openEvents']> = {
  mouseenter: true,
  focus: true,
}

export const TOOLTIP_CLOSE_HOVER_BLUR: NonNullable<ITooltip['closeEvents']> = {
  mouseleave: true,
  blur: true,
}

interface TooltipProps {
  children?: ReactNode
  icon?: ReactNode
  label?: ReactNode
  className?: string
  wrapperClassName?: string
  placement?: 'left-arrow' | 'right-arrow' | 'top-arrow' | 'bottom-arrow'
  inline?: boolean
  maxWidth?: number
  surfaceStyle?: CSSProperties
  zIndex?: number
  positionStrategy?: TooltipPositionStrategy
  openEvents?: ITooltip['openEvents']
  closeEvents?: ITooltip['closeEvents']
  /** Keep the tooltip open when the mouse moves into it (allows clicking links inside) */
  clickable?: boolean
}

const Tooltip = ({
  children,
  icon,
  label,
  className,
  wrapperClassName,
  placement = 'top-arrow',
  inline = false,
  maxWidth = 160,
  surfaceStyle,
  zIndex = 1999,
  positionStrategy = 'fixed',
  openEvents,
  closeEvents,
  clickable = false,
}: TooltipProps) => {
  const tooltipId = useId()
  const [portalReady, setPortalReady] = useState(false)

  useLayoutEffect(() => {
    setPortalReady(true)
  }, [])

  let place: 'top' | 'bottom' | 'left' | 'right'
  switch (placement) {
    case 'bottom-arrow':
      place = 'bottom'
      break
    case 'left-arrow':
      place = 'left'
      break
    case 'right-arrow':
      place = 'right'
      break
    case 'top-arrow':
    default:
      place = 'top'
      break
  }

  const triggerContent = inline ? (
    children
  ) : (
    <>
      {icon && <span>{icon}</span>}
      {label && <span>{label}</span>}
    </>
  )

  const tooltipContent = inline ? label : children

  const tooltipSurface = (
    <ReactTooltip
      id={tooltipId}
      place={place}
      positionStrategy={positionStrategy}
      clickable={clickable}
      {...(openEvents != null ? { openEvents } : {})}
      {...(closeEvents != null ? { closeEvents } : {})}
      className="tooltip-surface"
      classNameArrow="tooltip-surface-arrow"
      style={{
        backgroundColor: 'var(--color-neutral-100)',
        color: 'var(--color-neutral-05)',
        maxWidth,
        zIndex,
        borderRadius: '6px',
        padding: '6px 10px',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.4,
        boxShadow:
          '0 1px 2px rgb(0 0 0 / 0.18), 0 6px 16px -4px rgb(0 0 0 / 0.22)',
        ...surfaceStyle,
      }}
    >
      {clickable ? (
        <span
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {tooltipContent}
        </span>
      ) : (
        tooltipContent
      )}
    </ReactTooltip>
  )

  return (
    <span className={wrapperClassName ?? 'inline-flex items-center relative'}>
      <span
        className={cn('group/tooltip relative inline-flex', className)}
        data-tooltip-id={tooltipId}
      >
        {triggerContent}
      </span>
      {portalReady ? createPortal(tooltipSurface, document.body) : null}
    </span>
  )
}

export default Tooltip
